import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiGet, apiPost } from '@/lib/api';

interface FileNode {
  id: string;
  name: string;
  type: 'file' | 'folder';
  folderId: string | null;
  children?: FileNode[];
}

interface FileItem {
  folderID: string | number;
  filePath: string;
  metadata?: {
    title?: string;
    keywords?: string[];
    summary?: string;
  };
}

interface ProjectFilesCache {
  fileTree: FileNode[];
  isLoaded: boolean;
  isLoading: boolean;
}

interface FilesState {
  // State
  projectFilesCache: Record<string, ProjectFilesCache>;
  fileNames: Record<string, any>; // Backward compatibility - empty object
  files: FileItem[];
  totalItems: number;
  isFilesLoading: boolean;
  // Actions
  loadProjectFiles: (projectId: string) => Promise<void>;
  getProjectFiles: (projectId: string) => FileNode[];
  loadFiles: (
    page: number,
    pageSize: number | null,
    projectId: string | null,
    condition?: any
  ) => Promise<void>;
  resetProjectFilesCache: (projectId?: string) => void;
  setFiles: (files: FileItem[], totalItems: number) => void;
}
export const useFilesStore = create<FilesState>()(
  devtools(
    (set, get) => ({
      // Initial state
      projectFilesCache: {},
      fileNames: {}, // Backward compatibility - empty object
      files: [],
      totalItems: 0,
      isFilesLoading: false,
      // Actions
      loadProjectFiles: async (projectId: string) => {
        const state = get();
        const cache = state.projectFilesCache[projectId];

        // 如果正在加载中，直接返回（防止并发请求）
        if (cache?.isLoading) {
          return;
        }

        // 如果已经加载过，直接返回（即使文件树为空）
        if (cache?.isLoaded) {
          return;
        }

        // 设置加载状态
        set({
          projectFilesCache: {
            ...state.projectFilesCache,
            [projectId]: {
              fileTree: cache?.fileTree || [],
              isLoaded: false,
              isLoading: true,
            },
          },
        });

        try {
          const response = await apiGet<any>(
            `/api/projects/${projectId}/files?recursive=true`
          );
          const data = response?.data || response || {};
          set({
            projectFilesCache: {
              ...get().projectFilesCache,
              [projectId]: {
                fileTree: data.files || [],
                isLoaded: true,
                isLoading: false,
              },
            },
          });
        } catch (error) {
          console.error('❌ Failed to fetch project files:', error);
          set({
            projectFilesCache: {
              ...get().projectFilesCache,
              [projectId]: {
                fileTree: [],
                isLoaded: false,
                isLoading: false,
              },
            },
          });
        }
      },
      getProjectFiles: (projectId: string) => {
        const cache = get().projectFilesCache[projectId];
        return cache?.fileTree || [];
      },
      loadFiles: async (
        page: number,
        pageSize: number | null,
        projectId: string | null,
        condition?: any
      ) => {
        set({ isFilesLoading: true });
        try {
          const requestBody: any = {
            page,
            pageSize,
          };
          if (condition !== undefined && !projectId) {
            requestBody.condition = {
              type: 'binary_logic',
              operator: 'and',
              children: condition,
            };
          }
          const url = projectId
            ? `/api/projects/${projectId}/files?page=${page}&pageSize=1000`
            : '/api/files/filter';
          let data: any = {};
          if (projectId) {
            const response = await apiGet<any>(url);
            data = response?.data || response || {};
          } else {
            const response = await apiPost<any>(url, requestBody);
            data = response?.data || response || {};
          }
          const files = data.files || data.data?.files || [];
          const total = data.totalCount || files.length;
          set({
            files,
            totalItems: total,
            isFilesLoading: false,
          });
        } catch (error) {
          console.error('Fetch files error:', error);
          set({ isFilesLoading: false });
          throw error;
        }
      },
      resetProjectFilesCache: (projectId?: string) => {
        if (projectId) {
          const state = get();
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [projectId]: _removed, ...rest } = state.projectFilesCache;
          set({ projectFilesCache: rest });
        } else {
          set({ projectFilesCache: {} });
        }
      },
      setFiles: (files, totalItems) => set({ files, totalItems }),
    }),
    { name: 'FilesStore' }
  )
);
